/**
 * Годовая оплата (docs/billing/plan.md §4b): места оплачиваются на год вперёд, участники
 * сверх оплаченных доплачиваются раз в месяц — долей годовой цены до конца срока, — а
 * уменьшить оплаченные места до конца срока нельзя.
 *
 * Чистые расчёты без БД — общие для сервера (когда и сколько списать) и веба (подсказки):
 * подсказка на странице обязана сходиться с суммой, которую потом спишет сервер.
 */
import {
  intervalMonths,
  isPerSeatPlan,
  planPeriodPriceRub,
  resolveBillingInterval,
  seatPriceRub,
  type BillingInterval,
  type PlanId,
} from './plans.js';

/** Строка оплаты в объёме, нужном для решения «идёт ли годовой срок». */
export interface AnnualTermRow {
  source: string;
  status: string;
  billingInterval?: string | null;
  currentPeriodStart?: Date | null;
  currentPeriodEnd?: Date | null;
  trialEndsAt: Date | null;
}

/**
 * Идёт оплаченный годовой срок подписки (plan.md §4b): строка CloudPayments с годовым
 * периодом, который ещё не кончился. `cancelled` — тоже срок: продление отключено, но места
 * и тариф оплачены до конца года. `live` — только с включённым продлением (`active`):
 * повысить тариф или докупить места отменённой подписке нечем. Даты срока после проверки
 * заданы — тип их сужает.
 *
 * Отменённый годовой ПРОБНЫЙ период — не срок: за него ничего не платили, и «год оплачен
 * до …» с запретами покупок там был бы неправдой. Признак — окно триала (период кончается
 * не позже его конца), а не статус: отмена переписывает `trialing` на `cancelled`.
 */
export function isAnnualTermActive<T extends AnnualTermRow>(
  billing: T | null | undefined,
  options: { now?: number; live?: boolean } = {},
): billing is T & { currentPeriodStart: Date; currentPeriodEnd: Date } {
  return (
    !!billing &&
    billing.source === 'cloudpayments' &&
    resolveBillingInterval(billing.billingInterval) === 'year' &&
    (billing.status === 'active' || (!options.live && billing.status === 'cancelled')) &&
    !!billing.currentPeriodStart &&
    !!billing.currentPeriodEnd &&
    billing.currentPeriodEnd.getTime() > (options.now ?? Date.now()) &&
    !(billing.trialEndsAt && billing.currentPeriodEnd.getTime() <= billing.trialEndsAt.getTime())
  );
}

/**
 * Места отменённого оплаченного года заморожены до конца срока (решение владельца 2026-10-17):
 * условия отменённой подписки не меняются — позвать платного участника сверх оплаченных можно,
 * только возобновив её. Число мест — потолок; null — срок не заморожен.
 */
export function frozenAnnualSeats(
  billing: (AnnualTermRow & { paidSeats: number | null }) | null | undefined,
  now: number = Date.now(),
): number | null {
  return isAnnualTermActive(billing, { now }) && billing.status === 'cancelled' && billing.paidSeats !== null
    ? Math.max(1, billing.paidSeats)
    : null;
}

/** Округление до копеек: суммы в журнале платежей — numeric(12,2). */
export function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

const ms = (d: Date | number): number => (typeof d === 'number' ? d : d.getTime());

/** Доля срока `[start, end]`, оставшаяся к `at`, — от 0 до 1. */
export function remainingShare(start: Date | number, end: Date | number, at: Date | number = Date.now()): number {
  return Math.min(1, Math.max(0, (ms(end) - ms(at)) / Math.max(1, ms(end) - ms(start))));
}

/** +N месяцев с клампом дня (31 января + 1 мес = 28/29 февраля, как у CloudPayments). */
export function addMonthsUtc(from: Date, months: number): Date {
  const d = new Date(from);
  const day = d.getUTCDate();
  d.setUTCDate(1);
  d.setUTCMonth(d.getUTCMonth() + months);
  const lastDay = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0)).getUTCDate();
  d.setUTCDate(Math.min(day, lastDay));
  return d;
}

/** Страховка цикла: годовой срок — двенадцать месяцев, больше границ у него не бывает. */
const MAX_TRUE_UP_BOUNDARIES = 24;

/**
 * Границы повтора доплаты за места годового срока — каждый месяц в день его начала.
 * Каждую считаем от начала срока, а не от прошлой границы: иначе 31 января → 28 февраля →
 * 28 марта, и день доплаты съезжал бы навсегда.
 *
 * `dueAt` — последняя наступившая граница: доплату за неё пора списать (null — первый месяц
 * срока ещё идёт). `nextAt` — ближайшая будущая до конца срока (null — дальше только
 * продление: последние места доплатятся вместе с ним).
 */
export function annualTrueUpSchedule(
  termStart: Date,
  termEnd: Date,
  now: Date,
): { dueAt: Date | null; nextAt: Date | null } {
  let dueAt: Date | null = null;
  for (let k = 1; k <= MAX_TRUE_UP_BOUNDARIES; k++) {
    const boundary = addMonthsUtc(termStart, k);
    if (boundary.getTime() >= termEnd.getTime()) {
      break;
    }
    if (boundary.getTime() > now.getTime()) {
      return { dueAt, nextAt: boundary };
    }
    dueAt = boundary;
  }
  return { dueAt, nextAt: null };
}

/**
 * Годовой срок подписки для страницы тарифов: оплаченные места, превышение и когда его
 * доплата спишется. Есть только у подписки с `interval: 'year'`.
 */
export interface AnnualSeatsSummary {
  termStart: string;
  termEnd: string;
  /** Оплачено мест на этот срок: при покупке и доплатами — только растёт. */
  paidSeats: number;
  /** Платных мест сейчас. */
  billableSeats: number;
  /** Максимум платных мест за срок, но не меньше оплаченных. */
  peakSeats: number;
  overSeats: number;
  /** Доплата за места сверх оплаченных до конца срока; null — в рублях не считается. */
  trueUpRub: number | null;
  /**
   * Место сверх оплаченных списывается сразу; если списание не прошло — когда повторим:
   * ближайшая месячная граница срока; null — вместе с продлением.
   */
  nextTrueUpAt: string | null;
}

/**
 * Сколько стоит одно место годового срока с `at` до его конца: доля годовой цены места на
 * ступени `seats` — та доля срока, что осталась. Место с первого дня срока стоит ровно
 * годовую цену. null — тариф не посеточный.
 */
export function annualSeatCostRub(
  plan: PlanId,
  seats: number,
  termStart: Date | number,
  termEnd: Date | number,
  at: Date | number,
): number | null {
  const price = seatPriceRub(plan, Math.max(1, seats), 'year');
  return price === null ? null : price * intervalMonths('year') * remainingShare(termStart, termEnd, at);
}

/**
 * Меньше скольких мест «Добавить места» не продаёт: места, уже занятые сверх оплаченных,
 * можно оплатить и без новых — не дожидаясь повтора доплаты; иначе хотя бы одно новое.
 */
export function annualSeatsMinimum(term: { paidSeats: number; peakSeats: number; overSeats: number }): number {
  return term.overSeats > 0 ? term.peakSeats : term.paidSeats + 1;
}

/**
 * «Добавить места» на идущий годовой срок: места, уже занятые сверх оплаченных, — с даты
 * добавления (`trueUpRub` — их доплата по журналу состава), новые сверх пика — с `at` до
 * конца срока; всё по годовой цене ступени итогового числа мест. Платёж — не меньше 1 ₽.
 * null — тариф не посеточный.
 */
export function annualSeatsChargeRub(params: {
  plan: PlanId;
  seats: number;
  peakSeats: number;
  trueUpRub: number;
  termStart: Date | number;
  termEnd: Date | number;
  at: Date | number;
}): { newSeats: number; seatPriceRub: number; extraRub: number; amountRub: number } | null {
  const price = seatPriceRub(params.plan, Math.max(1, params.seats), 'year');
  const cost = annualSeatCostRub(params.plan, params.seats, params.termStart, params.termEnd, params.at);
  if (price === null || cost === null) {
    return null;
  }
  const newSeats = Math.max(0, params.seats - params.peakSeats);
  const extraRub = round2(newSeats * cost);
  return { newSeats, seatPriceRub: price, extraRub, amountRub: Math.max(1, round2(params.trueUpRub + extraRub)) };
}

/**
 * Зачёт при переходе с помесячной оплаты на годовую: неиспользованная доля того, что за
 * текущий месяц РЕАЛЬНО списали.
 */
export function annualSwitchCreditRub(paidMonthlyRub: number, remainingFraction: number): number {
  return round2(Math.max(0, paidMonthlyRub) * Math.min(1, Math.max(0, remainingFraction)));
}

/**
 * Переход с помесячной оплаты на годовую (plan.md §4b): годовой срок начинается сегодня —
 * годовая цена за вычетом зачёта остатка месяца плюс долг за участников, ставших платными в
 * закрываемом куске месяца (продление по месяцу его уже не посчитает). Не меньше 1 ₽.
 */
export function annualSwitchChargeRub(params: {
  annualRub: number;
  creditRub: number;
  seatArrearsRub: number;
}): number {
  return Math.max(1, round2(params.annualRub - params.creditRub + params.seatArrearsRub));
}

/** Строка оплаты в объёме, нужном для решения «что спишет следующее списание». */
export interface NextChargeRow {
  status: string | null;
  billingInterval?: string | null;
  pendingInterval?: string | null;
  paidSeats?: number | null;
}

/** Интервал следующего списания: заказанная смена (год → месяц в конце срока) или текущий. */
export function nextChargeInterval(row: Pick<NextChargeRow, 'billingInterval' | 'pendingInterval'>): BillingInterval {
  return resolveBillingInterval(row.pendingInterval ? row.pendingInterval : row.billingInterval);
}

/**
 * Что спишет следующее списание подписки: интервал, места и сумма (plan.md §4b).
 *
 * Продление считается по нынешнему составу — и помесячное, и годовое: места, купленные на
 * прошлый год заранее, на следующий не переносятся. Исключение — годовой пробный период:
 * его первое списание открывает годовой срок, и места, выбранные при подключении, оно
 * оплачивает целиком, даже если команда ещё не собрана.
 */
export function nextChargeQuote(
  row: NextChargeRow,
  plan: PlanId,
  billableSeats: number,
): { interval: BillingInterval; seats: number | null; amountRub: number | null } {
  const interval = nextChargeInterval(row);
  if (!isPerSeatPlan(plan)) {
    return { interval, seats: null, amountRub: planPeriodPriceRub(plan, 1, interval) };
  }
  const chosen =
    row.status === 'trialing' && interval === 'year' && resolveBillingInterval(row.billingInterval) === 'year'
      ? (row.paidSeats ?? 0)
      : 0;
  const seats = Math.max(1, billableSeats, chosen);
  return { interval, seats, amountRub: planPeriodPriceRub(plan, seats, interval) };
}
